import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';

// THE TWO-AXIS ACCESS CONTROL — one component, every write surface.
// (2026-08-08, DECISIONS.md "Two visibility axes, and only one of them needed a column".)
//
// ⚠ TWO LABELLED ROWS, NEVER A NINE-CELL GRID. The whole point of the ruling is that these are
// INDEPENDENT questions, and the three-pill row it replaces hid exactly that: "Private + AI"
// read as one setting between "Sealed" and "Shared", so the axes looked like points on a
// severity scale. They are not. A grid would say "independent" and then make the user solve a
// 3x3 puzzle to write a note. Two rows of three says it and stays a two-tap choice.
//
// ⚠ DO NOT WRITE COPY PROMISING AN APPROVAL LIST. `private_ai` means "only a model this
// instance runs", which today is the owner's model. There is no per-account approved-model set
// — no table, no session field, no per-object list — and saying otherwise would be the second
// time this project shipped a privacy promise the code did not keep. (The first was the old
// `sealed` blurb, which is why migration 016's backfill honours the promise over the behaviour.)
//
// The mirror of backend/lib/access.ts's axesToFlags lives here, and it is the ONLY client-side
// copy: Write.js's `tierFlags` and scripts/transcribe_upload.js's table were both deleted when
// this landed.

export const ALL_GROUP_OBJECT_ID = '00000000-0000-0000-0000-000000000002';

export const HUMAN_ACCESS = [
  { id: 'public', label: 'Anyone', blurb: 'Public. Readable by anyone with the link, including people who are not signed in.' },
  { id: 'group', label: 'A group', blurb: 'Readable by the members of a group you choose.' },
  { id: 'private', label: 'Only me', blurb: 'Readable by you alone.' },
];

export const MODEL_ACCESS = [
  { id: 'public_ai', label: 'Any model', blurb: 'May be sent to a third-party model to suggest tags, relations and claims.' },
  { id: 'private_ai', label: 'Local only', blurb: 'Only a model this instance runs. Never leaves this machine. (Today that is the owner’s model — there is no per-account approval list yet.)' },
  { id: 'sealed', label: 'No model', blurb: 'Never sent to any model, including a local one. Nothing here can be extracted or suggested from.' },
];

// The client mirror of axesToFlags. `group` with no group is refused rather than degraded —
// telling someone their writing is shared with a group when it is private is worse than an error.
export function accessToFlags({ human, model, groups }) {
  if (human === 'public') return { model_access: model, visibility_targets: [ALL_GROUP_OBJECT_ID] };
  if (human === 'group') return { model_access: model, visibility_targets: (groups || []).filter(Boolean) };
  return { model_access: model, visibility_targets: [] };
}

export const accessIsComplete = (a) => a.human !== 'group' || (a.groups || []).filter(Boolean).length > 0;

// A one-line summary for the locked state after mint, where the full control is gone but the
// choice still has to be legible.
export function describeAccess({ human, model }) {
  const who = HUMAN_ACCESS.find((h) => h.id === human)?.label || human;
  const which = MODEL_ACCESS.find((m) => m.id === model)?.label || model;
  return `${who} · ${which}`;
}

const rowStyle = { display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-2)', flexWrap: 'wrap' };
const labelStyle = { fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)', minWidth: '96px' };

// Exported for surfaces that already own a richer HUMAN-axis control and only need the model
// one — CreateObject has the arbitrary-target picker, which is strictly more expressive than
// this component's three-way row, so it keeps that and takes only this half.
export function Segmented({ options, value, onChange, ariaLabel, disabled }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        border: '1px solid var(--wv-border-strong)',
        borderRadius: 'var(--wv-r-pill)',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
      }}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((o, i) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          disabled={disabled}
          title={o.blurb}
          onClick={() => onChange(o.id)}
          style={{
            padding: '3px 10px',
            fontSize: 'var(--wv-fs-xs)',
            border: 'none',
            borderLeft: i === 0 ? 'none' : '1px solid var(--wv-border-strong)',
            background: value === o.id ? 'var(--wv-accent)' : 'transparent',
            color: value === o.id ? 'var(--wv-bg)' : 'var(--wv-muted)',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}

/**
 * value: { human, model, groups? }   onChange: (next) => void
 *
 * `compact` drops the per-choice blurb line, for surfaces where the control sits inside a row
 * of other controls rather than owning its own block.
 */
export default function AccessControl({ value, onChange, disabled = false, compact = false }) {
  const [groups, setGroups] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);

  // Only fetched when the group row is actually reachable — a surface that never offers group
  // should not make a request for it on every mount.
  useEffect(() => {
    if (disabled) return;
    apiFetch('/api/groups')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed'))))
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => setLoadFailed(true));
  }, [disabled]);

  const set = (patch) => onChange({ ...value, ...patch });
  const selected = (value.groups || []).filter(Boolean);
  const humanBlurb = HUMAN_ACCESS.find((h) => h.id === value.human)?.blurb;
  const modelBlurb = MODEL_ACCESS.find((m) => m.id === value.model)?.blurb;

  const toggleGroup = (objectId) =>
    set({
      groups: selected.includes(objectId)
        ? selected.filter((g) => g !== objectId)
        : [...selected, objectId],
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wv-sp-2)' }}>
      <div style={rowStyle}>
        <span style={labelStyle}>Who can read this</span>
        <Segmented
          options={HUMAN_ACCESS}
          value={value.human}
          onChange={(human) => set({ human })}
          ariaLabel="who can read this"
          disabled={disabled}
        />
      </div>

      {value.human === 'group' && (
        <div style={{ ...rowStyle, paddingLeft: '96px' }}>
          {loadFailed && <span style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-neg)' }}>Could not load your groups.</span>}
          {!loadFailed && groups.length === 0 && (
            <span style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)' }}>
              You are not in any group yet.{' '}
              <Link to="/groups" style={{ color: 'var(--wv-accent)' }}>Create one on the groups page</Link>, or choose
              another audience.
            </span>
          )}
          {groups.map((g) => (
            <button
              key={g.object_id}
              type="button"
              disabled={disabled}
              onClick={() => toggleGroup(g.object_id)}
              className="wv-chip"
              style={{
                background: selected.includes(g.object_id) ? 'var(--wv-accent)' : 'transparent',
                color: selected.includes(g.object_id) ? 'var(--wv-bg)' : 'var(--wv-muted)',
                cursor: disabled ? 'default' : 'pointer',
              }}
            >
              {g.name}
            </button>
          ))}
          {/* Said out loud rather than left to a failed save: `group` with nothing chosen is
              refused at the door on purpose, because degrading it to private would tell someone
              their writing is shared when it is not. */}
          {groups.length > 0 && selected.length === 0 && (
            <span style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)' }}>Pick at least one.</span>
          )}
        </div>
      )}

      <div style={rowStyle}>
        <span style={labelStyle}>Which models may read it</span>
        <Segmented
          options={MODEL_ACCESS}
          value={value.model}
          onChange={(model) => set({ model })}
          ariaLabel="which models may read it"
          disabled={disabled}
        />
      </div>

      {!compact && (
        <div style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)', lineHeight: 1.5 }}>
          {humanBlurb} {modelBlurb}
        </div>
      )}
    </div>
  );
}
